import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useCancelOrder } from '../hooks/use-order-mutations';
import {
  cancelOrderSchema,
  type CancelOrderFormInput,
  type CancelOrderFormOutput,
} from '../schemas/cancel-order-schema';
import { cancelBlocker, orderConflictKey, refundable } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['reason', 'restock', 'refund']);

export interface CancelOrderDialogProps {
  storeId: string;
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Reason and restock flag. A paid order is cancelled with its refund in one step when the member
 * may refund (`refund: true`, recorded in the same transaction); otherwise money received blocks
 * the cancel as before.
 */
export function CancelOrderDialog({ storeId, order, open, onOpenChange }: CancelOrderDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const { can } = usePermissions();
  const format = useFormat();
  const message = useErrorMessage();
  const cancel = useCancelOrder(storeId);
  const paidBlock = cancelBlocker(order) === 'paid';
  const refundWith = paidBlock && can('orders.refund');
  const blocker = refundWith ? null : cancelBlocker(order);
  const form = useForm<CancelOrderFormInput, unknown, CancelOrderFormOutput>({
    resolver: useI18nResolver(cancelOrderSchema),
    defaultValues: { reason: '', restock: true, refund: refundWith },
  });

  const close = () => {
    onOpenChange(false);
    cancel.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await cancel.mutateAsync({
        id: order.id,
        body: {
          reason: values.reason,
          restock: values.restock,
          ...(values.refund ? { refund: true } : {}),
        },
      });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const conflict = orderConflictKey(error, 'orders:cancel.errors.conflict');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('orders:cancel.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:cancel.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {blocker ? (
              <Alert role="status">
                <AlertDescription>{t(`orders:cancel.blocked.${blocker}`)}</AlertDescription>
              </Alert>
            ) : null}
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:cancel.reason')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t('orders:cancel.reasonPlaceholder')}
                      disabled={Boolean(blocker)}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="restock"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      disabled={Boolean(blocker)}
                    />
                  </FormControl>
                  <FormLabel className="font-normal">{t('orders:cancel.restock')}</FormLabel>
                </FormItem>
              )}
            />
            {refundWith ? (
              <FormField
                control={form.control}
                name="refund"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1 rounded-md border p-3">
                    <div className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox checked={field.value} disabled aria-readonly />
                      </FormControl>
                      <FormLabel className="font-normal">
                        {t('orders:cancel.refundAmount', {
                          amount: format.money(refundable(order)),
                        })}
                      </FormLabel>
                    </div>
                    <FormDescription>{t('orders:cancel.refundHint')}</FormDescription>
                  </FormItem>
                )}
              />
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.back')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={cancel.isPending || Boolean(blocker)}
              >
                {cancel.isPending ? t('orders:cancel.submitting') : t('orders:cancel.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
